import { Button, Dialog, DetectionZoneCardinalDirection, Facility, GenerateLaneLinesRequest, isValidLaneWindowHours, LaneGenerations, LaneGenerationScope, LaneLine, MAX_LANE_WINDOW_HOURS, getLaneGenerationScopeDisplayName } from "@app/shared";
import cn from "classnames";
import React, { useEffect, useState } from "react";
import { toastSuccess } from "../../../helpers";
import { ICoordsHelper } from "../../../helpers/coordsHelpers";
import { colorForLaneNumber, detectLaneLineStreetName, generateUniqueLaneName } from "../../../helpers/laneLineHelpers";
import { calculateCardinalDirection } from "../../../helpers/mapHelpers";
import { CheckboxInput, EnumSelect, NumericInput, Select } from "../inputs";

type GenerateLaneLinesDialogProps = {
    open: boolean;
    facility: Facility;
    defaultDeviceId?: string;
    fromDate?: Date;
    existingLaneLines: LaneLine[];
    coordsHelper: ICoordsHelper;
    getNextTempId: () => number;
    onApplySuggestions: (generated: LaneLine[], existing: LaneLine[], replaceExisting: boolean) => void;
    onClose: () => void;
    hideScopeSelection?: boolean;
};

const LOOKBACK_OPTIONS = [24, 48, 72];

const CCW_ORDER: DetectionZoneCardinalDirection[] = [
    DetectionZoneCardinalDirection.North,
    DetectionZoneCardinalDirection.NorthWest,
    DetectionZoneCardinalDirection.West,
    DetectionZoneCardinalDirection.SouthWest,
    DetectionZoneCardinalDirection.South,
    DetectionZoneCardinalDirection.SouthEast,
    DetectionZoneCardinalDirection.East,
    DetectionZoneCardinalDirection.NorthEast,
];

export const GenerateLaneLinesDialog: React.FC<GenerateLaneLinesDialogProps> = ({
    open,
    facility,
    defaultDeviceId,
    fromDate,
    existingLaneLines,
    coordsHelper,
    getNextTempId,
    onApplySuggestions,
    onClose,
    hideScopeSelection,
}) => {
    const devices = facility.devices ?? [];
    const [scope, setScope] = useState<LaneGenerationScope>(defaultDeviceId ? LaneGenerationScope.Device : LaneGenerationScope.Facility);
    const [deviceId, setDeviceId] = useState<string | undefined>(defaultDeviceId ?? devices[0]?.id);
    const [lookbackHours, setLookbackHours] = useState<number | undefined>(24);
    const [replaceExisting, setReplaceExisting] = useState(false);
    const [phase, setPhase] = useState<"form" | "confirm">("form");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<string | undefined>();

    useEffect(() => {
        if (!open) return;
        setScope(defaultDeviceId ? LaneGenerationScope.Device : LaneGenerationScope.Facility);
        setDeviceId(defaultDeviceId ?? devices[0]?.id);
        setLookbackHours(24);
        setReplaceExisting(false);
        setPhase("form");
        setLoading(false);
        setMessage(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const buildLanes = async (raw: LaneLine[]): Promise<LaneLine[]> => {
        const dirs = raw.map((l) =>
            l.points && l.points.length >= 2
                ? calculateCardinalDirection(l.points[0], l.points[1], coordsHelper)
                : DetectionZoneCardinalDirection.North
        );

        const present = CCW_ORDER.filter((d) => dirs.includes(d));
        const numberByDir = new Map<DetectionZoneCardinalDirection, number>();
        present.forEach((d, i) => numberByDir.set(d, i + 1));

        const namesInPlay: LaneLine[] = replaceExisting ? [] : [...existingLaneLines];
        const built: LaneLine[] = [];

        for (let i = 0; i < raw.length; i++) {
            const l = raw[i];
            const dir = dirs[i];
            const approachNumber = numberByDir.get(dir) ?? 1;

            let name = l.name ?? "";
            try {
                const detected = await detectLaneLineStreetName(l, dir, coordsHelper);
                if (detected?.formattedName) name = detected.formattedName;
            } catch {
                // keep the server compass name as fallback
            }
            name = generateUniqueLaneName(name, namesInPlay);

            const lane = new LaneLine({
                id: getNextTempId().toString(),
                name,
                turnType: l.turnType,
                deviceId: l.deviceId ?? defaultDeviceId,
                points: l.points,
                laneNumber: approachNumber,
                color: colorForLaneNumber(approachNumber),
                facilityId: undefined,
            });
            lane.isExplicitlyCompleted = true;
            namesInPlay.push(lane);
            built.push(lane);
        }

        return built;
    };

    const runGenerate = async () => {
        setLoading(true);
        setMessage(undefined);
        try {
            const request = new GenerateLaneLinesRequest({
                scope,
                deviceIds: scope === LaneGenerationScope.Device && deviceId ? [deviceId] : undefined,
                lookbackHours: lookbackHours ?? 24,
                fromDate: fromDate?.toISOString(),
                $type: GenerateLaneLinesRequest.$type,
            });

            const result = await LaneGenerations.generate(facility.id, request);

            if (!result) {
                setMessage("The request failed. Please try again.");
                setPhase("form");
                return;
            }

            if (!result.enough) {
                setMessage(result.message ?? "Not enough vehicle data to generate lane lines.");
                setPhase("form");
                return;
            }

            const built = await buildLanes(result.laneLines ?? []);

            onApplySuggestions(built, existingLaneLines, replaceExisting);

            toastSuccess(`Generated ${built.length} lane line${built.length === 1 ? "" : "s"} — review, use "Align to Map" if they look shifted off the road, then Save.`);
            onClose();
        } catch {
            setMessage("Failed to generate lane lines. Please try again.");
            setPhase("form");
        } finally {
            setLoading(false);
        }
    };

    const handleOk = () => {
        if (loading) return;
        if (phase === "confirm") {
            void runGenerate();
            return;
        }
        if (replaceExisting && existingLaneLines.length > 0) {
            setPhase("confirm");
            return;
        }
        void runGenerate();
    };

    const handleCancel = () => {
        if (phase === "confirm") {
            setPhase("form");
            return;
        }
        onClose();
    };

    const hoursValid = isValidLaneWindowHours(lookbackHours);
    const okButtonText = loading ? "Generating…" : phase === "confirm" ? "Replace & Generate" : "Generate";
    const okDisabled = loading || (scope === LaneGenerationScope.Device && !deviceId) || !hoursValid;

    return (
        <Dialog
            show={open}
            title="Generate Lane Lines from Traffic"
            onOkClick={handleOk}
            onCancelClick={handleCancel}
            okButtonText={okButtonText}
            disableOkButton={okDisabled}
            showCancelButton={true}
        >
            {phase === "confirm" ? (
                <div className="flex flex-col gap-3 w-full">
                    <p className="text-sm">
                        This will delete the <strong>{existingLaneLines.length}</strong> existing lane line
                        {existingLaneLines.length === 1 ? "" : "s"} and replace them with the generated suggestions.
                    </p>
                    <p className="text-xs text-gray-500">
                        Existing lane lines are removed only after new ones are successfully generated, and nothing is saved until you click Save.
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-4 w-full">
                    {fromDate ? (
                        <p className="text-sm text-gray-600">
                            Generating from the traffic this device has collected since{" "}
                            <strong>{fromDate.toLocaleString()}</strong>. Review and adjust the suggestions before saving.
                        </p>
                    ) : (
                        <>
                            <p className="text-sm text-gray-600">
                                Generate lane lines automatically from recent vehicle trajectories at this intersection. Review and adjust the
                                suggestions before saving.
                            </p>

                            {!hideScopeSelection && (
                                <>
                                    <EnumSelect
                                        label="Use targets from"
                                        enumType={LaneGenerationScope}
                                        value={scope}
                                        onChange={(v) => setScope((v ?? LaneGenerationScope.Facility) as LaneGenerationScope)}
                                        nameToDisplayConverter={getLaneGenerationScopeDisplayName}
                                    />

                                    {scope === LaneGenerationScope.Device && (
                                        <Select
                                            label="Device"
                                            value={deviceId}
                                            onChange={(e) => setDeviceId(e.target.value)}
                                            options={devices.map((d) => ({ label: d.name ?? "Device", value: d.id ?? "" }))}
                                        />
                                    )}
                                </>
                            )}

                            <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium">Time window</span>
                                <div className="flex gap-2">
                                    {LOOKBACK_OPTIONS.map((h) => (
                                        <Button
                                            key={h}
                                            className={cn("flex-1", { "border-brand-secondary border-2": lookbackHours === h })}
                                            onClick={() => setLookbackHours(h)}
                                        >
                                            {h}h
                                        </Button>
                                    ))}
                                </div>
                                <div className="mt-1 flex items-center gap-2">
                                    <NumericInput value={lookbackHours} allowFloat={false} onChange={(v) => setLookbackHours(v)} placeholder="Custom" className="w-24" />
                                    <span className="text-xs text-gray-500">hours (1–{MAX_LANE_WINDOW_HOURS}, up to 10 days)</span>
                                </div>
                                {!hoursValid && <span className="text-xs text-red-600">Enter between 1 and {MAX_LANE_WINDOW_HOURS} hours.</span>}
                            </div>
                        </>
                    )}

                    <CheckboxInput
                        label="Replace existing lane lines"
                        checked={replaceExisting}
                        onChange={(e) => setReplaceExisting(e.target.checked)}
                    />

                    {message && <p className="text-sm text-red-600">{message}</p>}
                </div>
            )}
        </Dialog>
    );
};
